import { Injectable, NgZone } from '@angular/core'
import {
  BehaviorSubject,
  Observable,
  fromEvent,
  merge,
  timer,
  Subject,
  interval,
} from 'rxjs'
import { debounceTime, takeUntil, tap, map } from 'rxjs/operators'

export interface IdleState {
  isIdle: boolean
  timeRemaining: number
  totalIdleTime: number
}

@Injectable({
  providedIn: 'root',
})
export class UserIdleService {
  private readonly DEFAULT_IDLE_TIME = 15 * 60 * 1000 // 15 minutes in milliseconds
  private readonly DEBOUNCE_TIME = 100 // 100ms debounce for user events

  private idleTimeMs = this.DEFAULT_IDLE_TIME

  private idleState$ = new BehaviorSubject<IdleState>({
    isIdle: false,
    timeRemaining: 0,
    totalIdleTime: 0,
  })

  private userActivity$ = new BehaviorSubject<boolean>(false)
  private destroy$ = new Subject<void>()
  private resetTimer$ = new Subject<void>()
  private isRunning = false
  private lastActivityTime = Date.now()
  private graceTimer: any
  private countdownTimer: any

  // Events to track for user activity
  private readonly USER_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'keydown',
    'mousewheel',
    'touchmove',
    'touchend',
    'focus',
  ]

  constructor(private ngZone: NgZone) {
    this.setupActivityListeners()
    this.setupVisibilityChangeListener()
  }

  /**
   * Get the current idle state as an observable
   */
  getIdleState(): Observable<IdleState> {
    return this.idleState$.asObservable()
  }

  /**
   * Get user activity observable
   */
  getUserActivity(): Observable<boolean> {
    return this.userActivity$.asObservable()
  }

  /**
   * Start monitoring user idle time
   * @param idleTimeMs - Time in milliseconds before user is considered idle (also used as countdown time)
   */
  startWatching(idleTimeMs?: number): void {
    if (this.isRunning) {
      this.stopWatching()
    }

    this.idleTimeMs = idleTimeMs || this.DEFAULT_IDLE_TIME
    this.isRunning = true
    this.lastActivityTime = Date.now()

    // Initialize with full idle time
    this.idleState$.next({
      isIdle: false,
      timeRemaining: this.idleTimeMs,
      totalIdleTime: 0,
    })

    this.startGracePeriodTimer()
  }

  /**
   * Stop monitoring user idle time
   */
  stopWatching(): void {
    this.isRunning = false
    this.clearTimers()
    this.resetIdleState()
  }

  /**
   * Reset idle state to active
   */
  resetIdleState(): void {
    this.idleState$.next({
      isIdle: false,
      timeRemaining: 0,
      totalIdleTime: 0,
    })
  }

  /**
   * Get current idle time in milliseconds
   */
  getCurrentIdleTime(): number {
    if (!this.isRunning) return 0
    return Date.now() - this.lastActivityTime
  }

  /**
   * Check if user is currently idle
   */
  isUserIdle(): boolean {
    return this.idleState$.value.isIdle
  }

  /**
   * Manually trigger user activity (useful for programmatic activity)
   */
  triggerUserActivity(): void {
    this.onUserActivity()
  }

  private setupActivityListeners(): void {
    // Create observables for all user events
    const eventObservables = this.USER_EVENTS.map((eventType) =>
      fromEvent(document, eventType, { passive: true }),
    )

    // Merge all event observables and debounce them
    merge(...eventObservables)
      .pipe(
        debounceTime(this.DEBOUNCE_TIME),
        takeUntil(this.destroy$),
        tap(() => this.onUserActivity()),
      )
      .subscribe()
  }

  private setupVisibilityChangeListener(): void {
    // Handle tab visibility changes
    fromEvent(document, 'visibilitychange')
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          if (!document.hidden) {
            // Tab became active - treat as user activity
            this.onUserActivity()
          }
        }),
      )
      .subscribe()
  }

  private onUserActivity(): void {
    this.lastActivityTime = Date.now()
    this.userActivity$.next(true)

    // Always reset to full idle time when user activity is detected
    this.ngZone.run(() => {
      this.idleState$.next({
        isIdle: false,
        timeRemaining: this.idleTimeMs,
        totalIdleTime: 0,
      })
    })

    // Restart the grace period timer if we're running
    if (this.isRunning) {
      this.clearTimers()
      this.startGracePeriodTimer()
    }
  }

  private startGracePeriodTimer(): void {
    this.clearTimers()

    // Wait 1 second before starting countdown
    this.graceTimer = setTimeout(() => {
      if (this.isRunning) {
        this.startCountdownTimer()
      }
    }, 1000)
  }

  private startCountdownTimer(): void {
    // Start countdown from full idle time
    this.ngZone.run(() => {
      this.idleState$.next({
        isIdle: true,
        timeRemaining: this.idleTimeMs,
        totalIdleTime: this.getCurrentIdleTime(),
      })
    })

    let timeRemaining = this.idleTimeMs

    this.countdownTimer = setInterval(() => {
      timeRemaining -= 1000

      if (timeRemaining <= 0) {
        timeRemaining = 0
        this.clearTimers()
      }

      this.ngZone.run(() => {
        this.idleState$.next({
          isIdle: true,
          timeRemaining: timeRemaining,
          totalIdleTime: this.getCurrentIdleTime(),
        })
      })

      if (timeRemaining <= 0) {
        // Countdown finished - emit final state
        return
      }
    }, 1000)
  }

  private clearTimers(): void {
    if (this.graceTimer) {
      clearTimeout(this.graceTimer)
      this.graceTimer = null
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }
  }

  /**
   * Configure idle detection settings
   * @param config - Configuration object
   */
  configure(config: { idleTimeMs?: number }): void {
    if (config.idleTimeMs) {
      this.idleTimeMs = config.idleTimeMs
    }
  }

  /**
   * Get current configuration
   */
  getConfiguration(): { idleTimeMs: number } {
    return {
      idleTimeMs: this.idleTimeMs,
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.clearTimers()
  }
}
